import { createContext, useContext, useLayoutEffect, useRef, type ReactNode } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { rehypeProse, remarkSafeMath } from "./markdownExtensions";
import { remarkBoundedStrong } from "./markdownStrong";
import { remarkWorkspaceLinks } from "./markdownWorkspaceLinks";
import { withTimestamps } from "./views/videoSeek";
import { findProsePaths, workspaceFileHref } from "./workspaceLinks";

let loading: Promise<typeof import("temml")> | null = null;

/** Load the shared typesetter on demand, retrying after failed fetches. */
function typesetter(): Promise<typeof import("temml")> {
	loading ??= import("temml").catch((err) => {
		loading = null;
		throw err;
	});
	return loading;
}

/** Keep formulas readable while the safe MathML typesetter loads. */
function Math({ tex, display }: { tex: string; display: boolean }) {
	const host = useRef<HTMLSpanElement>(null);
	useLayoutEffect(() => {
		if (host.current) host.current.textContent = tex;
		let live = true;
		typesetter()
			.then(({ default: temml }) => {
				if (live && host.current) temml.render(tex, host.current, { displayMode: display, throwOnError: false, trust: false });
			})
			.catch((err: unknown) => console.error("temml failed to load", err));
		return () => { live = false; };
	}, [tex, display]);
	return <span className={display ? "math-display" : "math-inline"} ref={host}>{tex}</span>;
}

export const WikilinkContext = createContext<((name: string) => void) | null>(null);

function Wikilink({ name, label }: { name: string; label: string }) {
	const open = useContext(WikilinkContext);
	if (!open) return <span className="wikilink-plain">{label}</span>;
	return <button type="button" className="wikilink" onClick={() => open(name)}>{label}</button>;
}

function prose(text: string, sessionId?: string): ReactNode[] {
	const out: ReactNode[] = [];
	let last = 0;
	for (const match of text.matchAll(/\[\[([^\]|]+)(?:\|([^\]]+))?\]\]/g)) {
		out.push(...plainText(text.slice(last, match.index), `p${match.index}`, sessionId));
		const name = match[1]!.trim();
		out.push(<Wikilink key={`w${match.index}`} name={name} label={match[2]?.trim() ?? name} />);
		last = match.index + match[0].length;
	}
	out.push(...plainText(text.slice(last), "end", sessionId));
	return out;
}

/** Add platform controls only to prose, outside existing anchors and protected nodes. */
function plainText(text: string, keyPrefix: string, sessionId?: string): ReactNode[] {
	if (!sessionId) return withTimestamps(text, keyPrefix);
	const out: ReactNode[] = [];
	let last = 0;
	findProsePaths(text).forEach(({ start, end, path }, n) => {
		const url = workspaceFileHref(sessionId, path);
		if (!url) return;
		out.push(...withTimestamps(text.slice(last, start), `${keyPrefix}-s${n}`));
		out.push(<WorkspaceFileLink key={`${keyPrefix}-f${n}`} url={url}>{path}</WorkspaceFileLink>);
		last = end;
	});
	out.push(...withTimestamps(text.slice(last), `${keyPrefix}-end`));
	return out;
}

function safeHref(href: string): string {
	return /^https?:\/\//i.test(href) ? href : "#";
}

function WorkspaceFileLink({ url, children }: { url: string; children: ReactNode }) {
	return <a href={url} download={decodeURIComponent(url.slice(url.lastIndexOf("/") + 1))}>{children}</a>;
}

/** The shared facade preserves platform contexts and visual wrappers. */
export function Markdown({ text, sessionId }: { text: string; sessionId?: string }) {
	const components: Components = {
		h1: ({ children }) => <h3>{children}</h3>,
		h2: ({ children }) => <h4>{children}</h4>,
		h3: ({ children }) => <h5>{children}</h5>,
		h4: ({ children }) => <h6>{children}</h6>,
		pre: ({ node, children }) => {
			const code = node?.children.find((child) => child.type === "element" && child.tagName === "code");
			const classes = code?.type === "element" ? code.properties.className : undefined;
			const lang = Array.isArray(classes) ? classes.find((name) => String(name).startsWith("language-")) : undefined;
			return <pre className="code-block" data-lang={lang ? String(lang).slice(9) : undefined}>{children}</pre>;
		},
		table: ({ children }) => <div className="md-table-wrap"><table>{children}</table></div>,
		a: ({ href = "", children }) => {
			const file = sessionId ? workspaceFileHref(sessionId, href) : null;
			return file ? <WorkspaceFileLink url={file}>{children}</WorkspaceFileLink>
				: <a href={safeHref(href)} target="_blank" rel="noreferrer noopener">{children}</a>;
		},
		span: ({ node, children }) => {
			const tex = node?.properties.dataTex;
			if (typeof tex === "string") return <Math tex={tex} display={node?.properties.dataDisplay === true} />;
			if (node?.properties.dataProse && typeof children === "string") return <>{prose(children, sessionId)}</>;
			return <span>{children}</span>;
		},
	};
	return <div className="md"><ReactMarkdown remarkPlugins={[remarkGfm, remarkSafeMath, remarkBoundedStrong, [remarkWorkspaceLinks, { sessionId }]]} rehypePlugins={[rehypeProse]} components={components} skipHtml>{text}</ReactMarkdown></div>;
}
